const defaultConfig = {
  mode: 'local',
  loggedIn: false,
  storageKey: 'outfitworks_wishlist',
  legacyStorageKeys: ['owWishlist'],
  apiBase: '',
};

const heartIconMarkup =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2" d="M12.001 20.305l-1.45-1.32C5.4 14.307 2 11.223 2 7.435 2 4.35 4.42 2 7.5 2c1.74 0 3.41.81 4.5 2.09C13.09 2.81 14.76 2 16.5 2 19.58 2 22 4.35 22 7.435c0 3.788-3.4 6.872-8.55 11.56l-1.449 1.31z"></path></svg>';

const config = { ...defaultConfig, ...(window.OWWishlistConfig || {}) };

const dispatchUpdate = (items, source = config.mode || 'local') => {
  window.dispatchEvent(
    new CustomEvent('ow:wishlist-updated', {
      detail: { items, source },
    })
  );
};

const formatMoney = (cents) => {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(cents || 0, window.Shopify.money_format || '${{amount}}');
  }

  const currencyCode = window.OWMoneyConfig?.currencyCode || 'KRW';

  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: currencyCode === 'KRW' ? 0 : 2,
  }).format((cents || 0) / 100);
};

const escapeHtml = (value = '') =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const normalizeItem = (item) => {
  if (!item) return null;

  if (typeof item === 'string') {
    return { id: null, handle: item };
  }

  if (typeof item !== 'object') return null;

  const handle = item.handle || '';
  const id = item.id != null && item.id !== '' ? String(item.id) : null;
  if (!handle && !id) return null;

  return {
    id,
    handle,
    title: item.title || '',
    url: item.url || (handle ? `/products/${handle}` : ''),
    image: item.image || '',
    price: item.price != null && item.price !== '' ? Number(item.price) : null,
    compareAtPrice:
      item.compareAtPrice != null && item.compareAtPrice !== '' ? Number(item.compareAtPrice) : null,
    available:
      typeof item.available === 'boolean'
        ? item.available
        : String(item.available || '').toLowerCase() === 'true',
  };
};

const uniqueItems = (items) => {
  const map = new Map();

  items.forEach((item) => {
    const normalized = normalizeItem(item);
    if (!normalized) return;

    // Prefer handle as the canonical key so legacy handle-only entries
    // and newer id+handle entries collapse to the same wishlist item.
    const key = normalized.handle || normalized.id;
    if (!key) return;

    map.set(key, normalized);
  });

  return Array.from(map.values());
};

class LocalStorageAdapter {
  constructor(storageKey, legacyKeys = []) {
    this.storageKey = storageKey;
    this.legacyKeys = Array.isArray(legacyKeys) ? legacyKeys : [];
    this.memoryItems = [];
    this.migrateLegacyData();
  }

  migrateLegacyData() {
    try {
      const currentRaw = window.localStorage.getItem(this.storageKey);
      if (currentRaw) {
        this.memoryItems = uniqueItems(JSON.parse(currentRaw));
        return;
      }
    } catch {
      // Ignore read issues and fall back to legacy keys / memory.
    }

    for (const legacyKey of this.legacyKeys) {
      try {
        const legacyRaw = window.localStorage.getItem(legacyKey);
        if (!legacyRaw) continue;

        const migratedItems = uniqueItems(JSON.parse(legacyRaw));
        this.memoryItems = migratedItems;
        window.localStorage.setItem(this.storageKey, JSON.stringify(migratedItems));
        break;
      } catch {
        // Ignore bad legacy payloads.
      }
    }
  }

  getItems() {
    try {
      const raw = window.localStorage.getItem(this.storageKey) || '[]';
      const items = uniqueItems(JSON.parse(raw));
      this.memoryItems = items;
      return items;
    } catch {
      return this.memoryItems;
    }
  }

  writeItems(items) {
    const normalized = uniqueItems(items);
    this.memoryItems = normalized;

    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(normalized));
    } catch {
      // Keep in-memory fallback for preview/storage-restricted contexts.
    }

    return normalized;
  }
}

// Placeholder adapter for a future customer metafield sync.
// Writing customer metafields requires authenticated Storefront/Admin access,
// which cannot be safely completed from Liquid/theme code alone.
class CustomerMetafieldAdapter {
  getItems() {
    return [];
  }

  writeItems(items) {
    return uniqueItems(items);
  }
}

class WishlistStorage {
  constructor(adapter) {
    this.adapter = adapter;
  }

  getItems() {
    return uniqueItems(this.adapter.getItems());
  }

  setItems(items) {
    return uniqueItems(this.adapter.writeItems(items));
  }

  hasItem(itemOrKey) {
    const key =
      typeof itemOrKey === 'object'
        ? String(itemOrKey?.id || itemOrKey?.handle || '')
        : String(itemOrKey || '');

    if (!key) return false;

    return this.getItems().some((item) => item.id === key || item.handle === key);
  }

  addItem(item) {
    const normalized = normalizeItem(item);
    if (!normalized) return this.getItems();

    const items = this.getItems();
    if (this.hasItem(normalized)) return items;

    return this.setItems([...items, normalized]);
  }

  removeItem(itemOrKey) {
    const key =
      typeof itemOrKey === 'object'
        ? String(itemOrKey?.id || itemOrKey?.handle || '')
        : String(itemOrKey || '');

    if (!key) return this.getItems();

    return this.setItems(this.getItems().filter((item) => item.id !== key && item.handle !== key));
  }

  toggleItem(item) {
    const normalized = normalizeItem(item);
    if (!normalized) return this.getItems();

    return this.hasItem(normalized) ? this.removeItem(normalized) : this.addItem(normalized);
  }
}

const adapters = {
  local: new LocalStorageAdapter(config.storageKey, config.legacyStorageKeys),
  metafield: new CustomerMetafieldAdapter(),
};

const storage = new WishlistStorage(adapters.local);

const getItemFromButton = (button) =>
  normalizeItem({
    id: button.dataset.productId,
    handle: button.dataset.productHandle,
    title: button.dataset.productTitle,
    url: button.dataset.productUrl,
    image: button.dataset.productImage,
    price: button.dataset.productPrice,
    compareAtPrice: button.dataset.productCompareAtPrice,
    available: button.dataset.productAvailable,
  });

const setButtonState = (button, forcedActive = null) => {
  if (!(button instanceof HTMLButtonElement)) return;

  const item = getItemFromButton(button);
  if (!item) return;

  const isActive = typeof forcedActive === 'boolean' ? forcedActive : storage.hasItem(item);
  button.classList.toggle('is-active', isActive);
  button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

  const label = button.querySelector('[data-wishlist-label]');
  if (label) {
    label.textContent = isActive
      ? button.dataset.wishlistLabelActive || 'Saved'
      : button.dataset.wishlistLabelDefault || 'Wishlist';
  }
};

const animateButton = (button) => {
  button.classList.remove('is-animating');
  void button.offsetWidth;
  button.classList.add('is-animating');
  window.setTimeout(() => button.classList.remove('is-animating'), 280);
};

const bindButton = (button) => {
  if (!(button instanceof HTMLButtonElement)) return;
  if (button.dataset.wishlistBound === 'true') return;

  button.dataset.wishlistBound = 'true';
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    const item = getItemFromButton(button);
    if (!item) return;

    const nextItems = storage.toggleItem(item);
    const active = nextItems.some((entry) => entry.id === item.id || entry.handle === item.handle);
    setButtonState(button, active);
    animateButton(button);
    dispatchUpdate(nextItems);
    syncButtons();
  });
};

const syncButtons = (root = document) => {
  root.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    bindButton(button);
    setButtonState(button);
  });
};

const fetchProduct = async (item) => {
  if (!item?.handle) return null;

  const response = await fetch(`/products/${item.handle}.js`);
  if (!response.ok) return null;

  const product = await response.json();

  return {
    id: String(product.id),
    handle: product.handle,
    title: product.title,
    url: product.url,
    image: product.featured_image,
    available: product.available,
    price: product.price,
    compareAtPrice: product.compare_at_price,
  };
};

const renderWishlistCard = (product) => {
  const hasComparePrice = product.compareAtPrice > product.price;
  const compareMarkup = hasComparePrice
    ? `<span class="wishlist-card__compare">${escapeHtml(formatMoney(product.compareAtPrice))}</span>`
    : '';

  const imageMarkup = product.image
    ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" class="product-media__image" loading="lazy">`
    : '<div class="ow-wishlist-card__image-placeholder"></div>';

  const statusMarkup = product.available ? '' : '<p class="wishlist-card__status">Sold out</p>';

  return `
    <li class="product-grid__item" data-product-id="${escapeHtml(product.id)}">
      <article class="product-card" data-product-id="${escapeHtml(product.id)}">
        <a href="${escapeHtml(product.url)}" class="product-card__link">
          <span class="visually-hidden">${escapeHtml(product.title)}</span>
        </a>
        <div
          class="product-card__content wishlist-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style"
          style="--quick-add-display: none; --quick-add-mobile-display: none;"
        >
          <div class="card-gallery" data-product-id="${escapeHtml(product.id)}">
            <a class="contents" href="${escapeHtml(product.url)}" aria-label="${escapeHtml(product.title)}">
              <div class="product-media-container media-fit product-media-container--image">
                ${imageMarkup}
              </div>
            </a>
          </div>
          <div class="wishlist-card__meta">
            <h3 class="wishlist-card__title">${escapeHtml(product.title)}</h3>
            <div class="wishlist-card__pricing">
                <span class="wishlist-card__price">${escapeHtml(formatMoney(product.price))}</span>
                ${compareMarkup}
            </div>
            ${statusMarkup}
          </div>
          <button
            type="button"
            class="product-card__wishlist-toggle"
            data-wishlist-toggle
            data-wishlist-context="wishlist-page"
            data-product-id="${escapeHtml(product.id)}"
            data-product-handle="${escapeHtml(product.handle)}"
            data-product-url="${escapeHtml(product.url)}"
            data-product-title="${escapeHtml(product.title)}"
            data-product-image="${escapeHtml(product.image || '')}"
            data-product-price="${escapeHtml(product.price)}"
            data-product-compare-at-price="${escapeHtml(product.compareAtPrice || '')}"
            data-product-available="${product.available}"
            data-wishlist-label-default="Wishlist"
            data-wishlist-label-active="Saved"
            aria-label="Wishlist: ${escapeHtml(product.title)}"
            aria-pressed="true"
          >
            <span class="wishlist-toggle__icon svg-wrapper" aria-hidden="true">${heartIconMarkup}</span>
          </button>
        </div>
      </article>
    </li>
  `;
};

const renderWishlistPage = async (section) => {
  const grid = section.querySelector('[data-wishlist-grid]');
  const emptyState = section.querySelector('[data-wishlist-empty]');
  const countElement = section.querySelector('[data-wishlist-count]');
  if (!grid) return;

  const items = storage.getItems();

  if (countElement) {
    countElement.textContent = `${items.length} ${items.length === 1 ? 'item' : 'items'}`;
  }

  if (items.length === 0) {
    grid.innerHTML = '';
    emptyState?.removeAttribute('hidden');
    return;
  }

  emptyState?.setAttribute('hidden', '');
  grid.innerHTML = '';

  const results = await Promise.allSettled(items.map((item) => fetchProduct(item)));
  const products = uniqueItems(
    results
    .filter((result) => result.status === 'fulfilled' && result.value)
    .map((result) => result.value)
  );

  if (products.length === 0) {
    storage.setItems([]);
    if (countElement) countElement.textContent = '0 items';
    emptyState?.removeAttribute('hidden');
    return;
  }

  grid.innerHTML = products.map(renderWishlistCard).join('');
  syncButtons(grid);
};

const mountWishlistPages = (root = document) => {
  root.querySelectorAll('[data-wishlist-page]').forEach((section) => {
    renderWishlistPage(section);
  });
};

const init = () => {
  syncButtons();
  mountWishlistPages();
};

window.addEventListener('storage', (event) => {
  if (event.key !== config.storageKey) return;
  syncButtons();
  mountWishlistPages();
});

window.addEventListener('ow:wishlist-updated', () => {
  syncButtons();
  mountWishlistPages();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

if (document.body) {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        syncButtons(node);
        if (node.matches?.('[data-wishlist-page]') || node.querySelector?.('[data-wishlist-page]')) {
          mountWishlistPages(node);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

window.OWWishlist = {
  config,
  adapters,
  storage,
  heartIconMarkup,
  formatMoney,
  getItems: () => storage.getItems(),
  setItems: (items) => storage.setItems(items),
  addItem: (item) => {
    const nextItems = storage.addItem(item);
    dispatchUpdate(nextItems);
    return nextItems;
  },
  removeItem: (itemOrKey) => {
    const nextItems = storage.removeItem(itemOrKey);
    dispatchUpdate(nextItems);
    return nextItems;
  },
  hasItem: (itemOrKey) => storage.hasItem(itemOrKey),
  toggleItem: (item) => {
    const nextItems = storage.toggleItem(item);
    dispatchUpdate(nextItems);
    return nextItems;
  },
  syncButtons,
  mountPage: mountWishlistPages,
};
